import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Baby, Heart, Smile, Users, Star, Check, ArrowRight } from "lucide-react";

const SKILLS = [
  { icon: Heart, title: "Understanding Feelings", body: "Naming happy, sad, mad, scared — and what to do with each." },
  { icon: Smile, title: "Safe Choices", body: "Age-appropriate ways to say yes, say no, and ask for help." },
  { icon: Users, title: "Respect & Kindness", body: "How we treat family, friends, teachers, and ourselves." },
  { icon: Star, title: "Following Instructions", body: "Listening skills that make school and home run smoother." },
  { icon: Users, title: "Positive Friendships", body: "What a good friend looks like — and being one yourself." },
  { icon: Heart, title: "Healthy Habits", body: "Sleep, food, movement, and feelings — the kid version." },
  { icon: Users, title: "Trusted Adults", body: "Identifying grown-ups you can go to when something isn't right." },
  { icon: Star, title: "Early Leadership", body: "Taking turns, speaking up, and helping younger kids." },
];

export default function LittleLeaders() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#6B21A8] text-white" data-testid="little-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5B90A]/20 via-transparent to-transparent" />
        <div className="container-x relative py-24 md:py-32 grid gap-10 md:grid-cols-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Early Development Track · Ages 5–9</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight">
              Little Leaders,<br />
              <span className="italic text-[#F5B90A]">Big Choices.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/80">
              Starting strong. Starting early. A playful, story-based program designed for younger boys to build the
              feelings, friendship, and decision-making skills they'll carry for life.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/enroll" className="btn-gold" data-testid="little-enroll"><ArrowRight className="h-4 w-4" /> Enroll Your Child</Link>
              <Link to="/contact" className="btn-outline-white" data-testid="little-contact">Ask a Question</Link>
            </div>
          </motion.div>
          <div className="md:col-span-4">
            <div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-8 text-center">
              <Baby className="h-14 w-14 mx-auto text-[#F5B90A]" strokeWidth={1.8} />
              <p className="mt-5 overline text-[#F5B90A]">A Separate Track</p>
              <p className="mt-2 font-display text-xl">Age-appropriate curriculum, caregiver participation, and lots of play.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT THEY LEARN */}
      <section className="bg-white py-20 md:py-24" data-testid="little-skills">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline text-[#6B21A8]">The Building Blocks</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">What our Little Leaders learn.</h2>
            <p className="mt-4 text-black/70 text-lg">
              Designed in partnership with early-childhood guidance, every lesson is play-based, story-driven, and
              paired with a simple take-home activity for families.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {SKILLS.map((s, i) => (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-editorial p-6" data-testid={`little-skill-${i}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F5B90A]/20 text-[#6B21A8]">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-black/70">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOT THE OLDER TRACK */}
      <section className="bg-[#F7F3EC] py-16" data-testid="little-note">
        <div className="container-x">
          <div className="mx-auto max-w-3xl rounded-lg bg-white border border-black/10 p-8 text-center">
            <h3 className="font-display text-2xl font-bold">A separate experience from our 10–17 program.</h3>
            <p className="mt-3 text-black/70">
              Little Leaders, Big Choices is designed specifically for younger boys. Our Change Choices: Strong Minds curriculum
              and other 10–17 programs are structured differently and are not part of this track.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/strong-minds" className="btn-outline">See the 10–17 Signature Program</Link>
              <Link to="/programs" className="btn-outline">All Programs</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAMILY PARTICIPATION */}
      <section className="bg-white py-20" data-testid="little-family">
        <div className="container-x grid gap-10 md:grid-cols-12 items-start">
          <div className="md:col-span-6">
            <span className="overline text-[#6B21A8]">Family-Powered Learning</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Families are part of every session.</h2>
            <p className="mt-5 text-black/70 text-lg">
              Each week ends with a caregiver guide and a family activity so the learning happens at home, too. You're not
              dropping your son off — you're growing alongside him.
            </p>
          </div>
          <div className="md:col-span-6 grid gap-3">
            {[
              "Weekly caregiver recap after every session",
              "Family-night workshops throughout the year",
              "Age-appropriate reading list and conversation starters",
              "Positive reinforcement system kids actually love",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg bg-[#F7F3EC] p-5" data-testid={`little-family-${i}`}>
                <Check className="h-5 w-5 mt-0.5 text-[#6B21A8] flex-shrink-0" />
                <p className="font-medium">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
